<template>
	<div class="w-full h-full flex flex-col items-center">
		<ProfilesBar
			:profiles="profiles"
			:selectedProfile="selectedProfile"
		/>

		<!-- DATES -->
		<div
			id="dates-container"
			class="w-full px-4"
		>
			<div
				class="btn-pills-group dates-days"
				v-for="group in datesMap"
				:key="`group-${group.name}`"
			>
				<button
					v-for="item in group.children"
					:key="`item-${item.value}`"
					class="btn btn-pill"
					:class="{ active: currentDateType === item.value }"
					:id="item.value"
					@click="SelectDateType(item.value)"
				>
					{{ item.label }}
				</button>
			</div>
		</div>

		<!-- PERIOD -->
		<hr />
		<div
			id="period-container"
			class="w-full px-4"
		>
			<label class="font-bold">PERÍODO</label>
			<div
				class="form-group mb-0 input-with-icon"
				:class="{ active: hasDate }"
			>
				<flatpickr
					v-model="dateRange"
					:config="datePickerConfig"
					class=""
					placeholder="SELECIONE O INTERVALO"
				/>
				<i class="fal fa-calendar-alt icon-calendar"></i>
			</div>
		</div>

		<!-- MODULES/MODALITIES/SCHEDULES -->
		<hr />
		<div
			id="schedules-container"
			class="w-full px-4"
		>
			<div
				class="status-wrapper"
				:class="{
					active: hasSchedules,
				}"
			>
				<button
					class="btn btn-dropdown"
					@click="ToggleSchedulesDropdown"
				>
					<span> MÓDULOS / MODALIDADES / AGENDAS </span>
					<i
						class="fas"
						:class="{
							'fa-chevron-right':
								!filters.opened.schedulesDropdown,
							'fa-chevron-down': filters.opened.schedulesDropdown,
						}"
					></i>
				</button>
				<div
					class="dropdown-list"
					v-if="filters.opened.schedulesDropdown"
				>
					<div class="w-full px-0 hierarchy-select pl-3">
						<div
							class="w-full flex items-center"
							@click.stop.prevent="CheckAll()"
						>
							<div class="flex-1 form-check py-1 level-1-color">
								<input
									type="checkbox"
									class=""
									id="select-all"
									name="select-all"
									:checked="filters.modules_select_all"
									@change="CheckAll()"
									hidden
								/>
								<label
									class="form-check-label"
									for="select-all"
								>
								</label>
								<span
									class="flex-1 text-uppercase text-ellipsis font-bold font-level font-level-1"
									>TODOS</span
								>
							</div>
						</div>
						<HierarchySelect
							ref="hierarchySelect"
							:value="filters.modules"
							keyName="modules"
							identifier="module"
							:level="1"
							:withSelectAll="false"
							@change="ChangeSchedulesHandler()"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- STATUSES -->
		<hr />
		<div
			id="statuses-container"
			class="w-full px-4"
		>
			<div
				class="status-wrapper"
				:class="{
					active: hasStatuses,
				}"
			>
				<button
					class="btn btn-dropdown"
					@click="ToggleStatusesDropdown"
				>
					<span> STATUS </span>
					<i
						class="fas"
						:class="{
							'fa-chevron-right':
								!filters.opened.statusesDropdown,
							'fa-chevron-down': filters.opened.statusesDropdown,
						}"
					></i>
				</button>
				<div
					class="dropdown-list"
					v-if="filters.opened.statusesDropdown"
				>
					<div
						v-for="status in filters.statuses"
						:key="status.value"
						class="w-full my-1"
					>
						<div
							class="form-check py-1"
							:class="status.value.toLowerCase()"
							@click.stop.prevent="SelectStatus(status)"
						>
							<input
								type="checkbox"
								class=""
								:id="status.value"
								:name="status.value"
								:checked="status.checked"
								hidden
							/>
							<label
								class="form-check-label ml-3"
								:for="status.value"
								@click.stop.prevent="SelectStatus(status)"
							>
							</label>
							<span
								class="flex-1 text-uppercase text-ellipsis font-bold"
							>
								{{ status.label }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- AUTHOR -->
		<hr />
		<div
			id="author-container"
			class="w-full flex flex-col items-center px-4"
		>
			<div class="form-group w-full">
				<input
					id="author-document"
					type="text"
					class="form-control"
					v-model="filters.author.document"
					v-mask="['###.###.###-##']"
					placeholder="AUTOR - CPF"
					@blur="BlurAuthorInput"
				/>
			</div>
			<div class="form-group w-full">
				<input
					id="author-name"
					type="text"
					class="form-control"
					v-model="filters.author.name"
					placeholder="AUTOR - NOME"
					@blur="BlurAuthorInput"
				/>
			</div>
		</div>

		<SaveProfileModal
			v-if="showSaveProfileModal"
			@close="showSaveProfileModal = false"
			@save="SaveProfile"
		/>
	</div>
</template>

<script>
	import moment from 'moment'
	import { mapGetters } from 'vuex'
	import Flatpickr from 'vue-flatpickr-component'
	import 'flatpickr/dist/flatpickr.css'
	import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
	import {
		useCopyObject,
		useEachRecursively,
		useGetSelectedsRecursively,
		useError,
		useConfirmation,
	} from '@/composables'

	import HierarchySelect from '@/components/shared/HierarchySelect'

	const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'

	const _DATES_MAP = [
		{
			name: 'days',
			children: [
				{ label: 'HOJE', value: 'today' },
				{ label: 'ONTEM', value: 'yesterday' },
				{ label: '24 HORAS', value: 'twenty_four_hours' },
			],
		},
		{
			name: 'weeks',
			children: [
				{ label: 'SEMANA ATUAL', value: 'current_week' },
				{ label: 'SEMANA ANTERIOR', value: 'last_week' },
				{ label: '7 DIAS', value: 'seven_days' },
			],
		},
		{
			name: 'months',
			children: [
				{ label: 'MÊS ATUAL', value: 'current_month' },
				{ label: 'MÊS ANTERIOR', value: 'last_month' },
				{ label: '30 DIAS', value: 'thirty_days' },
			],
		},
		{
			name: 'years',
			children: [
				{ label: 'ANO ATUAL', value: 'current_year' },
				{ label: 'ANO ANTERIOR', value: 'last_year' },
				{ label: '365 DIAS', value: 'three_sixty_five_days' },
			],
		},
	]

	const _STATUSES = [
		{ label: 'SUSPENSO', value: 'SUSPENDED', checked: false },
		{ label: 'CANCELADO', value: 'CANCELLED', checked: false },
		{ label: 'ATENDIMENTO', value: 'ATTENDING', checked: false },
		{ label: 'NÃO ATENDIDO', value: 'NO_CALL_RESPONSE', checked: false },
		{ label: 'PENDENTE', value: 'PENDING', checked: false },
		{ label: 'ENVIADO', value: 'SENT', checked: false },
		{ label: 'RECEBIDO', value: 'RECEIVED', checked: false },
		{ label: 'NÃO RESPONDEU', value: 'NO_ANSWERED', checked: false },
		{ label: 'NÃO RECEBIDO', value: 'NO_RECEIVED', checked: false },
		{ label: 'CONFIRMADO', value: 'CONFIRMED', checked: false },
		{ label: 'RESOLVIDO', value: 'RESOLVED', checked: false },
	]

	export default {
		name: 'Filters',
		components: {
			Flatpickr,
			HierarchySelect,
		},
		props: {
			showSidebar: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				initializing: false,
				datesMap: [..._DATES_MAP],
				profiles: {},
				baseModules: [],
				filters: {
					statuses: [..._STATUSES],
					modules_select_all: false,
					modules: [],
					date: {
						init: null,
						end: null,
						type: null,
					},
					author: {
						document: '',
						name: '',
					},
					opened: {
						schedulesDropdown: false,
						statusesDropdown: false,
					},
					showSidebar: true,
				},
				dateRange: [],
				datePickerConfig: {},
				datePickerInstance: null,
				selectedProfile: 'current_profile',
				lastSelectedProfile: null,
				saveFiltersDebounce: false,
				showSaveProfileModal: false,
			}
		},
		computed: {
			...mapGetters('auth', [
				'requester_selected_id',
				'requester_selected_object',
			]),
			currentDateType() {
				return this.filters.date.type
			},
			hasDate() {
				return !!(
					this.filters.date.init ||
					this.filters.date.end ||
					this.filters.date.type
				)
			},
			hasSchedules() {
				const { schedules } = useGetSelectedsRecursively(
					this.filters.modules
				)

				return schedules?.length > 0
			},
			hasStatuses() {
				return this.filters.statuses.filter(s => s.checked).length > 0
			},
			hasAuthor() {
				return !!(
					this.filters.author.name || this.filters.author.document
				)
			},
			hasAtLeastOneFilter() {
				return (
					this.hasDate ||
					this.hasSchedules ||
					this.hasStatuses ||
					this.hasAuthor
				)
			},
		},
		watch: {
			hasAtLeastOneFilter: {
				handler(val) {
					this.$emit('hasAtLeastOneFilter', val)
				},
				immediate: true,
			},
			showSidebar: {
				handler(val) {
					this.filters.showSidebar = val

					this.SaveFiltersDebounce()
				},
				immediate: true,
			},
		},
		methods: {
			GetProfiles() {
				return window.api
					.call('post', '/filters/get-profiles', {
						workgroup_id: this.requester_selected_object.id,
						module: 'journey-cases',
					})
					.then(async ({ status, data }) => {
						if (status !== 200) {
							return useError('Erro ao obter os filtros!')
						}

						this.profiles = { ...data.profiles }

						await this.$nextTick()

						this.lastSelectedProfile = data.lastSelectedProfile

						this.SelectProfile(
							this.lastSelectedProfile ?? 'current_profile',
							true
						)
					})
			},
			async SelectProfile(profileName, bypass = false) {
				let profile = this.profiles[profileName]
				if (!profile) return

				const isToggle = this.selectedProfile === profileName
				if (isToggle) {
					this.selectedProfile = 'current_profile'
					profile = this.profiles.current_profile
				} else {
					this.selectedProfile = profileName
				}

				await this.LoadProfileFilters(useCopyObject(profile))
				await this.SetLastSelectedProfile(this.selectedProfile)

				if (!bypass) {
					this.Update(false)
					this.ApplyFilters()
				}
			},
			async LoadProfileFilters(profile) {
				this.filters = {
					statuses: profile.filters.statuses,
					modules: useCopyObject(this.baseModules),
					modules_select_all: profile.filters.modules_select_all,
					date: profile.filters.date,
					author: profile.filters.author,
					opened: profile.filters.opened,
					showSidebar: profile.filters.showSidebar,
				}

				if (profile.filters.date.type) {
					this.LoadDateTypeFilter(profile.filters.date.type)
				}

				await this.LoadModulesSelection(profile)
			},
			async LoadDateTypeFilter(dateType) {
				const { date_init, date_end } = await this.CalculateDateType(
					dateType
				)

				const previousInitDate = this.filters.date.init
				const previousEndDate = this.filters.date.end
				const isDateInitDifferent = previousInitDate !== date_init
				const isDateEndDifferent = previousEndDate !== date_end

				this.filters.date.init = date_init
				this.filters.date.end = date_end

				this.datePickerInstance.setDate([
					new Date(this.filters.date.init),
					new Date(this.filters.date.end),
				])

				if (isDateInitDifferent || isDateEndDifferent) {
					this.SaveFiltersDebounce(this.selectedProfile)
				}
			},
			async LoadModulesSelection(profile) {
				await this.ClearSelecteds()

				profile.filters.modules.forEach(profileModule => {
					// if (!profileModule.selected) return

					const filtersModule = this.filters.modules.find(
						m => m.id === profileModule.id
					)
					if (!filtersModule) return
					filtersModule.selected = profileModule.selected
					filtersModule.expanded = profileModule.expanded

					profileModule.children.forEach(profileModality => {
						// if (!profileModality.selected) return

						const filtersModality = filtersModule.children.find(
							m => m.id === profileModality.id
						)
						if (!filtersModality) return
						filtersModality.selected = profileModality.selected
						filtersModality.expanded = profileModality.expanded

						profileModality.children.forEach(profileSchedule => {
							// if (!profileSchedule.selected) return

							const filtersSchedule =
								filtersModality.children.find(
									s => s.id === profileSchedule.id
								)
							if (!filtersSchedule) return
							filtersSchedule.selected = profileSchedule.selected
						})
					})
				})
			},
			async ClearSelecteds() {
				useEachRecursively(this.filters.modules, filtersItem => {
					filtersItem.selected = false
				})
			},
			async SetLastSelectedProfile(profile = 'current_profile') {
				if (this.initializing) return
				if (this.lastSelectedProfile === profile) return

				return window.api
					.call('post', '/filters/set-last-selected-profile', {
						workgroup_id: this.requester_selected_object.id,
						module: 'journey-cases',
						profile,
					})
					.then(async ({ status }) => {
						if (status !== 200) {
							return useError(
								'Erro ao memorizar o último perfil selecionado!'
							)
						}

						this.lastSelectedProfile = profile
					})
			},
			Update(autoSave = true) {
				this.ValidateAuthorInputs()

				if (autoSave) {
					this.SaveFiltersDebounce(this.selectedProfile)
				}
			},
			ApplyFilters() {
				this.$emit('apply', { filters: useCopyObject(this.filters) })
			},
			ClearFilters() {
				this.UnselectProfile()
				this.ClearPeriodInput()
				this.BlurAuthorInput()
				this.filters.date = {
					init: null,
					end: null,
					type: null,
				}

				this.filters.modules_select_all = false
				useEachRecursively(this.filters.modules, item => {
					item.selected = false
				})

				this.filters.statuses = this.filters.statuses.map(s => {
					s.checked = false
					return s
				})

				this.filters.author = {
					document: '',
					name: '',
				}

				this.filters.opened = {
					schedulesDropdown: this.filters.opened.schedulesDropdown,
					statusesDropdown: this.filters.opened.statusesDropdown,
				}

				this.SaveFiltersDebounce()
			},
			async UnselectProfile() {
				this.selectedProfile = 'current_profile'
			},
			ClearPeriodInput() {
				this.datePickerInstance.clear()
				this.datePickerInstance.close()
			},
			BlurAuthorInput() {
				this.UnselectProfile()

				this.ValidateAuthorInputs()

				this.Update()
			},
			ValidateAuthorInputs() {
				document
					.querySelectorAll('#author-container input')
					.forEach(input => {
						this.AuthorIndicatorHandler(input)
					})
			},
			async AuthorIndicatorHandler(input) {
				await this.$nextTick()

				const parent = input.parentElement

				if (input.value.length > 0) {
					parent.classList.add('active')
				} else {
					parent.classList.remove('active')
				}
			},
			SaveFiltersDebounce(profile = 'current_profile') {
				if (this.saveFiltersDebounce)
					clearTimeout(this.saveFiltersDebounce)

				this.saveFiltersDebounce = setTimeout(() => {
					this.SaveFilters(profile)
				}, 1000)
			},
			async SaveFilters(
				profile = 'current_profile',
				withConfirmation = false
			) {
				return window.api
					.call('post', '/filters/save-filters', {
						workgroup_id: this.requester_selected_object.id,
						module: 'journey-cases',
						filters: this.filters,
						profile,
					})
					.then(async ({ status }) => {
						if (status !== 200) {
							return useError('Erro ao salvar os filtros!')
						}

						if (withConfirmation) useConfirmation()

						const profileToUpdate = this.profiles[profile]

						if (!profileToUpdate) {
							this.GetProfiles()
						} else {
							profileToUpdate.filters = useCopyObject(
								this.filters
							)

							this.lastSelectedProfile = profile
						}
					})
			},
			SaveProfiles() {
				this.showSaveProfileModal = true
			},
			async SaveProfile(profile = 'current_profile') {
				await this.SaveFilters(profile, true)
				await this.UnselectProfile()
				await this.SelectProfile(profile, true)
			},
			async SelectDateType(dateType) {
				await this.UnselectProfile()

				const isToggle = this.filters.date.type === dateType

				this.ResetDate()

				if (isToggle) {
					this.Update()
					this.ApplyFilters()

					return
				}

				const { date_init, date_end } = await this.CalculateDateType(
					dateType
				)

				this.filters.date.init = date_init
				this.filters.date.end = date_end
				this.filters.date.type = dateType

				this.datePickerInstance.setDate([
					new Date(date_init),
					new Date(date_end),
				])

				this.Update()
				this.ApplyFilters()
			},
			ResetDate() {
				this.filters.date.init = null
				this.filters.date.end = null
				this.filters.date.type = null

				this.ClearPeriodInput()
			},
			async CalculateDateType(dateType) {
				let filters = {
					date_init: null,
					date_end: null,
					time_init: '00:00:00',
					time_end: '23:59:59',
				}

				switch (dateType) {
					// DAYS
					case 'today':
						filters.date_init = moment()
						filters.date_end = moment()
						break
					case 'yesterday':
						filters.date_init = moment()
							.subtract(1, 'd')
							.hour(0)
							.minute(0)
							.second(0)
						filters.date_end = moment()
							.subtract(1, 'd')
							.hour(23)
							.minute(59)
							.second(59)
						break
					case 'twenty_four_hours':
						filters.date_init = moment().subtract(24, 'h')
						filters.date_end = moment()

						filters.time_init = moment()
							.format(dateTimeFormat)
							.substring(11, 19)
						filters.time_end = moment()
							.format(dateTimeFormat)
							.substring(11, 19)
						break
					// WEEKS
					case 'current_week':
						filters.date_init = moment().startOf('week')
						filters.date_end = moment().endOf('week')
						break
					case 'last_week':
						filters.date_init = moment()
							.startOf('week')
							.subtract(7, 'd')
						filters.date_end = moment()
							.endOf('week')
							.subtract(7, 'd')
						break
					case 'seven_days':
						filters.date_init = moment().subtract(7, 'd')
						filters.date_end = moment()
						break
					// MONTHS
					case 'current_month':
						filters.date_init = moment().startOf('month')
						filters.date_end = moment().endOf('month')
						break
					case 'last_month':
						filters.date_init = moment()
							.subtract(1, 'M')
							.startOf('month')
						filters.date_end = moment()
							.subtract(1, 'M')
							.endOf('month')
						break
					case 'thirty_days':
						filters.date_init = moment().subtract(30, 'd')
						filters.date_end = moment()
						break
					// YEARS
					case 'current_year':
						filters.date_init = moment().startOf('year')
						filters.date_end = moment().endOf('year')
						break
					case 'last_year':
						filters.date_init = moment()
							.subtract(1, 'y')
							.startOf('year')
						filters.date_end = moment()
							.subtract(1, 'y')
							.endOf('year')
						break
					case 'three_sixty_five_days':
						filters.date_init = moment().subtract(365, 'd')
						filters.date_end = moment()
						break
				}

				filters.date_init = filters.date_init.format(dateTimeFormat)
				filters.date_end = filters.date_end.format(dateTimeFormat)

				filters.date_init = filters.date_init
					.replace('T', ' ')
					.replace(/\d{2}:\d{2}:\d{2}/, filters.time_init)
				filters.date_end = filters.date_end
					.replace('T', ' ')
					.replace(/\d{2}:\d{2}:\d{2}/, filters.time_end)

				return filters
			},
			ToggleSchedulesDropdown() {
				this.filters.opened.schedulesDropdown =
					!this.filters.opened.schedulesDropdown
				this.Update()
			},
			ToggleStatusesDropdown() {
				this.filters.opened.statusesDropdown =
					!this.filters.opened.statusesDropdown
				this.Update()
			},
			SelectStatus(status) {
				this.UnselectProfile()

				status.checked = !status.checked

				this.Update()
			},
			async CheckAll(forceState = null) {
				const newState = !this.filters.modules_select_all
				this.filters.modules_select_all = newState

				await useEachRecursively(this.filters.modules, item => {
					item.selected =
						forceState !== null ? !!forceState : newState
				})

				this.Update()
			},
			ChangeSchedulesHandler() {
				this.UnselectProfile()

				this.Update()
			},
			SetupDatePicker() {
				this.datePickerConfig = {
					allowInput: true,
					dateFormat: 'Y-m-d',
					altFormat: 'd/m/Y',
					altInput: true,
					altInputClass: 'form-control  flatpickr-alt-input',
					locale: Portuguese,
					mode: 'range',
					wrap: true,
					onChange: () => {
						this.filters.date.type = null
					},
					onClose: selectedDates => {
						const [init, end] = selectedDates

						if (!init) return
						if (!end) return

						this.filters.date.init =
							moment(init)
								.startOf('day')
								.format(dateTimeFormat) ?? null
						this.filters.date.end =
							moment(end).endOf('day').format(dateTimeFormat) ??
							null

						this.Update()
					},
					onReady: (selectedDates, dateStr, instance) => {
						if (
							!instance.calendarContainer.querySelector(
								'.flatpickr-clear'
							)
						) {
							this.datePickerInstance = instance

							const clearBtn = document.createElement('button')
							clearBtn.textContent = 'LIMPAR'
							clearBtn.type = 'button'
							clearBtn.className = 'flatpickr-clear btn mt-1'
							clearBtn.onclick = () => {
								this.filters.date.init = null
								this.filters.date.end = null

								this.ClearPeriodInput()

								this.Update()
							}

							const clearContainer = document.createElement('div')
							clearContainer.className =
								'flatpickr-clear-container'
							clearContainer.appendChild(clearBtn)

							instance.calendarContainer
								.querySelector('.flatpickr-footer')
								?.appendChild(clearContainer) ||
								instance.calendarContainer.appendChild(
									clearContainer
								)
						}
					},
				}
			},
			GetFiltersHierarchy() {
				return window.api
					.call(
						'get',
						`/messages/filters-hierarchy/${this.requester_selected_id}`
					)
					.then(async ({ status, data }) => {
						if (status !== 200) {
							return useError(
								'Erro ao obter os filtros de Módulo/Modalidade/Agenda!'
							)
						}

						this.baseModules =
							await this.StructFiltersHierarchyRecursively([
								...data.filters_hierarchy,
							])
					})
			},
			async StructFiltersHierarchyRecursively(hierarchy) {
				hierarchy = await Promise.all(
					hierarchy.map(async item => {
						const newItem = {
							id: item.id,
							name: item.name,
							selected: false,
							expanded: false,
							...this.GetCurrentStructure(item),
						}

						if (newItem.children) {
							newItem.children =
								await this.StructFiltersHierarchyRecursively(
									newItem.children
								)

							delete newItem[newItem.oldChildrenKey]
							delete newItem.oldChildrenKey
						} else {
							delete newItem.children
						}

						return newItem
					})
				)

				return hierarchy
			},
			GetCurrentStructure(item) {
				const current = {}

				if (item.modalities && Array.isArray(item.modalities)) {
					current.key = 'modules'
					current.children = item.modalities
					current.identifier = 'module'
					current.oldChildrenKey = 'modalities'
				} else if (item.schedules && Array.isArray(item.schedules)) {
					current.key = 'modalities'
					current.children = item.schedules
					current.identifier = 'modality'
					current.oldChildrenKey = 'schedules'
				} else {
					current.key = 'schedules'
					current.children = null
					current.identifier = 'schedule'
				}

				return current
			},
		},
		async beforeMount() {
			this.SetupDatePicker() // MANTENHA NESSA POSIÇÃO SE NÃO, NÃO RENDERIZA DIREITO
			this.initializing = true

			await this.GetFiltersHierarchy()
			await this.GetProfiles()
			this.initializing = false
			this.ApplyFilters()
		},
	}
</script>

<style lang="scss" scoped>
	div.active {
		position: relative;

		&::before {
			content: '';
			height: 1rem;
			width: 1rem;
			background-color: var(--pendingActiveBackgroundColor);
			border-radius: 50%;
			position: absolute;
			top: -7px;
			right: -6px;
			z-index: 10;
		}
	}

	.btn-dropdown {
		width: 100%;
		display: flex;
		justify-content: space-between;
	}

	.dropdown-list {
		width: 100%;
		display: flex;
		flex-direction: column;
		border: 1px solid var(--hr);
		border-bottom-left-radius: 0.5rem;
		border-bottom-right-radius: 0.5rem;
		overflow: hidden;
		padding: 0.3rem 0;
	}

	#period-container {
		color: var(--fontColor);

		.input-with-icon {
			position: relative;

			input.form-control {
				padding: 1rem 1.6rem 1rem 1.3rem;
			}
		}

		.input-with-icon .icon-calendar {
			position: absolute;
			right: 10px;
			top: 50%;
			transform: translateY(-50%);
			font-size: 1rem;
			pointer-events: none;
		}
	}

	:deep(#period-container .form-group input.form-control) {
		padding: 0.8rem 1.6rem 0.8rem 1.3rem;
	}
	// :deep(#period-container .form-group input.form-control[readonly]) {
	// 	background-color: var(--background) !important;
	// }

	#author-container {
		#author-name {
			text-transform: uppercase;
		}
	}
</style>
